// Desk build only: inside Route Desk's "HE Products" panel an officer must
// not be able to log out, change PIN, activate an account or sign in as
// someone else (Thijs, 2026-10-12). Everything here applies only when
// isInRouteDeskFrame() is true; outside a frame nothing changes.

import { matchPath } from "react-router-dom";

// Pages that cannot be opened inside the panel.
export const DESK_BLOCKED_PATHS = [
  "/", // Activate account
  "/set-pin",
  "/activate-by-qrcode",
  "/logout",
  "/change-pin",
  "/change-pin-success",
];

// Where to send a blocked page, or null when the page may open. Matched the
// same way App.js's <Route exact path={DESK_BLOCKED_PATHS}> matches (react-
// router's own matchPath: any letter case, an optional trailing slash).
// Never returns a blocked page itself, so it cannot loop: /dashboard and
// /login are both allowed.
export const deskBlockedRedirect = (pathname, { inFrame, signedIn }) => {
  if (!inFrame) return null;
  if (!matchPath(pathname || "", { path: DESK_BLOCKED_PATHS, exact: true })) return null;
  return signedIn ? "/dashboard" : "/login";
};

// The last 9 digits of a phone number, ignoring spaces, "+" and other
// characters: "+256 700 000 001", "0700000001" and "256700000001" all give
// "700000001". Null when there are fewer than 9 digits.
export const lastNineDigits = (phone) => {
  if (typeof phone !== "string") return null;
  const digits = phone.replace(/\D/g, "");
  return digits.length >= 9 ? digits.slice(-9) : null;
};

// True when the signed-in user is not the officer Route Desk says should be
// here: not an employee, or a different phone number. A user without a
// known phone counts as the wrong account (fail closed).
export const isWrongDeskAccount = (user, routeDeskPhone) => {
  if (!user || user.contact_type !== "employee") return true;
  const mine = lastNineDigits(user.phone_number);
  const expected = lastNineDigits(routeDeskPhone);
  return !mine || !expected || mine !== expected;
};

// What the wrong-account guard (components/DeskAccountGuard) does now:
//   "wait"      nothing to compare yet: outside a frame, signed out, no
//               phone from Route Desk (or a superadmin's manual sign-in),
//               or the profile is still loading (a profile left over from
//               an earlier sign-in must not be judged)
//   "ok"        the signed-in employee is the one Route Desk expects
//   "sign-out"  wrong account: sign out and go to Sign In
export const deskGuardDecision = ({ inFrame, signedIn, isFetching, user, routeDeskPhone }) => {
  if (!inFrame || !signedIn || !routeDeskPhone || isFetching || !user) return "wait";
  return isWrongDeskAccount(user, routeDeskPhone) ? "sign-out" : "ok";
};

// The guard's verdict on the signed-in account, shared with DeskOrderingFor
// so the ?ordering_for= CHE is applied only to a confirmed account (never
// to a wrong account just before it is signed out):
//   "unknown"   not checked yet (signed out, no answer from Route Desk,
//               profile still loading)
//   "ok"        Route Desk's phone matches this employee
//   "manual"    Route Desk said manual (superadmin): no check
//   "mismatch"  wrong account; the guard is signing it out
// A plain module-level value with listeners: the guard is its only writer.
let accountStatus = "unknown";
const statusListeners = new Set();

export const getDeskAccountStatus = () => accountStatus;

export const setDeskAccountStatus = (status) => {
  if (status === accountStatus) return;
  accountStatus = status;
  statusListeners.forEach((listener) => listener(status));
};

// Returns the unsubscribe function.
export const subscribeDeskAccountStatus = (listener) => {
  statusListeners.add(listener);
  return () => statusListeners.delete(listener);
};

// The status for a guard decision (deskGuardDecision) and whether Route Desk
// said manual.
export const deskAccountStatusFrom = ({ manual, decision }) => {
  if (manual) return "manual";
  if (decision === "ok") return "ok";
  if (decision === "sign-out") return "mismatch";
  return "unknown";
};

// May DeskOrderingFor apply the ?ordering_for= CHE now? Inside the panel
// only after the guard confirmed the account (fails closed); outside a frame
// there is no such CHE anyway (stashOrderingForFromUrl), and nothing changes.
export const mayApplyOrderingFor = (inFrame, status) =>
  !inFrame || status === "ok" || status === "manual";
